import {
  Icon,
  IContextualMenuItem,
  IContextualMenuListProps,
  IContextualMenuProps,
  IRenderFunction,
  ISearchBoxStyles,
  IStyle,
  KeyCodes,
  mergeStyles,
} from '@fluentui/react';
import { Button, ClassNamesFromIStyles, IH2OTheme, Link, SearchBox, useClassNames, useTheme } from '@h2oai/ui-kit';
import React from 'react';

interface ISelectableCalloutStyles {
  contextualMenu: IStyle;
}

const selectableCalloutStyles = (theme: IH2OTheme): Partial<ISelectableCalloutStyles> => ({
    contextualMenu: {
      width: 236,
      outlineColor: theme.semanticColors?.contentBackground, // TODO: Remove once theme is used.
    },
  }),
  getButtonStyles = (iconOnly?: boolean, iconSize?: number, minimal?: boolean) => {
    return iconOnly
      ? {
          root: {
            border: 'none',
            height: 20,
            minHeight: '',
            minWidth: 0,
            padding: 0,
          },
          rootFocused: {
            backgroundColor: 'transparent',
          },
          rootHovered: {
            backgroundColor: 'transparent',
          },
          rootPressed: {
            backgroundColor: 'transparent',
          },
          menuIcon: iconOnly ? { display: 'none' } : undefined,
          icon: iconSize
            ? {
                root: { fontSize: iconSize, marginRight: '0px !important', padding: '11px 10px' },
                fontSize: iconSize,
                marginRight: '0px !important',
              }
            : undefined,
        }
      : minimal
      ? {
          root: { width: 'auto', border: 'none' },
          icon: { display: 'none' },
          menuIcon: { fontSize: 12 },
        }
      : { root: { width: '189px' } };
  },
  filteredItemsStyle: React.CSSProperties = {
    width: '100%',
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchBoxStyles: ISearchBoxStyles = {
    root: { margin: '8px' },
  };

export type SelectableItemsCalloutProps = {
  bottomLinkText?: string;
  buttonIconName?: string;
  buttonText?: string;
  buttonTitle?: string;
  calloutNoItemsIconName?: string;
  calloutNoItemsText?: string;
  emptyItemsText?: string;
  // Exclusive with minimal.
  iconOnly?: boolean;
  iconSize?: number;
  isReadOnly?: boolean;
  menuItems: IContextualMenuItem[];
  // Exclusive with iconOnly.
  minimal?: boolean;
  onBottomLinkClick?: () => void;
  openContextMenuIfNoItems?: boolean;
  searchBoxPlaceholderText?: string;
  searchable?: boolean;
  styles?: Partial<ISelectableCalloutStyles>;
};

const SelectableItemsCallout = (props: SelectableItemsCalloutProps) => {
  const {
    buttonTitle,
    buttonText,
    buttonIconName,
    calloutNoItemsText,
    calloutNoItemsIconName,
    menuItems,
    searchable = true,
    searchBoxPlaceholderText,
    bottomLinkText,
    onBottomLinkClick,
    iconOnly = false,
    minimal = false,
    iconSize,
    isReadOnly = false,
    openContextMenuIfNoItems = false,
    emptyItemsText,
  } = props;
  const [searchText, setSearchText] = React.useState('');
  const items = React.useMemo(() => {
    if (!menuItems.length) {
      if (!openContextMenuIfNoItems) return [];
      return [
        {
          key: 'no_contexmenu_results',
          text: '',
          onClick: () => {},
          canCheck: false,
          isChecked: false,
        },
      ];
    }
    if (!searchText) return menuItems;
    const searchTextLower = searchText.toLowerCase();
    const filteredItems = menuItems.filter(
      (item) =>
        (item.text && item.text.toLowerCase().includes(searchTextLower)) ||
        (item.secondaryText && item.secondaryText.toLowerCase().includes(searchTextLower))
    );
    if (filteredItems.length) return filteredItems;
    return [
      {
        key: 'no_results',
        onRender: () => (
          <div key="no_results" style={filteredItemsStyle}>
            <Icon iconName={calloutNoItemsIconName || 'SearchIssue'} title={calloutNoItemsText || 'No items found.'} />
            <span>{calloutNoItemsText || 'No items found.'}</span>
          </div>
        ),
      },
    ];
  }, [menuItems, searchText]);

  const classNames = useClassNames<ISelectableCalloutStyles, ClassNamesFromIStyles<ISelectableCalloutStyles>>(
    'selectableCallout',
    selectableCalloutStyles,
    props.styles
  );
  const renderMenuList = (
    menuListProps?: IContextualMenuListProps,
    defaultRender?: IRenderFunction<IContextualMenuListProps>
  ) => {
    const hasEmptyFirstItem = menuListProps?.items?.[0].key === 'no_contexmenu_results';
    return (
      <div className={classNames.contextualMenu}>
        {searchable && !hasEmptyFirstItem ? (
          <div style={{ borderBottom: '1px solid #ccc' }}>
            <SearchBox
              placeholder={searchBoxPlaceholderText || 'Search items'}
              onAbort={() => {
                setSearchText(``);
              }}
              onChange={(_event, newValue) => {
                setSearchText(newValue || ``);
              }}
              onKeyDown={(e) => {
                /* Key Up, but we are not at the beginning of the text: stop event propagation to prevent ContextualMenu to focus */
                const input = e.target as HTMLInputElement;
                if (input.selectionStart && input.selectionStart > 0 && e.which === KeyCodes.up) {
                  e.stopPropagation();
                }
                /* Key Down, but we are not at the end of the text: stop event propagation to prevent ContextualMenu to focus */
                if (input.selectionStart !== input.value.length && e.which === KeyCodes.down) {
                  e.stopPropagation();
                }
              }}
              styles={searchBoxStyles}
              value={searchText}
            />
          </div>
        ) : null}
        {!hasEmptyFirstItem ? defaultRender?.(menuListProps) : null}
        {hasEmptyFirstItem && emptyItemsText ? (
          <p style={{ marginLeft: 12, marginRight: 12 }}>{emptyItemsText}</p>
        ) : null}
        {!hasEmptyFirstItem || (hasEmptyFirstItem && emptyItemsText) ? <br /> : null}
        {bottomLinkText && onBottomLinkClick ? (
          <Link style={{ marginLeft: 12 }} onClick={onBottomLinkClick}>
            {bottomLinkText}
          </Link>
        ) : null}
      </div>
    );
  };

  const menuProps: IContextualMenuProps = {
    onRenderMenuList: renderMenuList,
    shouldFocusOnMount: true,
    focusZoneProps: {
      /* Allow up and down arrows to move focus out of the SearchBox */
      shouldInputLoseFocusOnArrowKey: () => true,
    },
    items,
    onDismiss: () => {
      setSearchText('');
    },
  };

  return (
    <Button
      title={iconOnly ? undefined : buttonTitle || 'Select items'}
      iconName={buttonIconName || 'Search'}
      text={iconOnly ? undefined : buttonText || 'No items selected'}
      menuIconName={iconOnly ? undefined : 'ChevronDown'}
      menuProps={menuProps}
      readOnly={isReadOnly}
      styles={getButtonStyles(iconOnly, iconSize, minimal)}
    />
  );
};

export default SelectableItemsCallout;

export type SelectableMenuItem = Omit<IContextualMenuItem, `canCheck` | `onClick` | `isChecked`>;

export const toMenuItem =
  (selectedItems: string[] | undefined, setNewSelectedItems: (newItems: string[]) => void) =>
  (item: SelectableMenuItem): IContextualMenuItem => {
    const activeIdx = !selectedItems ? -1 : selectedItems.indexOf(item.key);
    return {
      ...(item as any),
      canCheck: true,
      isChecked: activeIdx !== -1,
      onClick: () => {
        setNewSelectedItems(
          // @ts-expect-error Array#toSpliced not defined in TypeScript<5.2.2
          activeIdx === -1 ? [...(selectedItems || []), item.key] : selectedItems.toSpliced(activeIdx, 1)
        );
      },
    };
  };

export type SelectedCountBadgeProps = {
  children?: React.ReactNode;
};

const selectedCountBadgeStyle = (theme: IH2OTheme): IStyle => ({
  backgroundColor: theme.palette?.yellow400,
  borderRadius: '99em',
  color: theme.palette?.black,
  display: 'inline-flex',
  paddingInline: `1em`,
  transform: 'scale(0.8)',
});
export const SelectedCountBadge = (props: SelectedCountBadgeProps) => {
  const theme = useTheme();
  const className = mergeStyles(selectedCountBadgeStyle(theme));
  return <div className={className}>{props.children}</div>;
};
